import { onAuthStateChanged, signOut as fbSignOut, type User } from 'firebase/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { auth } from './firebase';
import { api } from './api';
import { clearCaches } from './native';
import { identify } from './analytics';

interface AuthState {
  user: User | null;
  ready: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState>({ user: null, ready: false, signOut: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const prev = useRef<User | null>(null);
  useEffect(() => {
    // Store-screenshot builds (VITE_SHOTS=1) show a signed-in sample account; normal builds drop this branch.
    if (import.meta.env.VITE_SHOTS === '1') {
      setUser({ uid: 'u-daniel', displayName: 'Daniel Whitfield', email: 'daniel@example.com', providerData: [] as User['providerData'] } as User);
      setReady(true);
      return;
    }
    return onAuthStateChanged(auth, (u) => {
      // Signed out (or account deleted): drop the offline copies so the next person on this phone sees nothing.
      if (prev.current && !u) {
        void clearCaches();
        identify(null);
      }
      prev.current = u;
      setUser(u);
      setReady(true);
      // The Worker upserts the account from the ID token; sync carries the Firebase display name across once.
      if (u) void api('POST', '/me/sync', u.displayName ? { displayName: u.displayName } : {}).catch(() => {});
    });
  }, []);
  return <Ctx.Provider value={{ user, ready, signOut }}>{children}</Ctx.Provider>;
}

// The native plugin keeps its own session (it picks the Google account next time), so end both.
async function signOut() {
  if (Capacitor.isNativePlatform()) await FirebaseAuthentication.signOut().catch(() => {});
  await fbSignOut(auth);
}

export const useAuth = () => useContext(Ctx);
